import { ColumnState, SortModelItem } from 'ag-grid-community'

interface TableStateStorage {
  columnState?: ColumnState[]
  filterModel?: Record<string, unknown>
  sortModel?: SortModelItem[]
}

const STORAGE_KEY_PREFIX = 'agGrid_tableState_'

/**
 * Check if localStorage is available and accessible
 * @returns true if localStorage is available, false otherwise
 */
const isLocalStorageAvailable = (): boolean => {
  try {
    const test = '__localStorage_test__'
    localStorage.setItem(test, test)
    localStorage.removeItem(test)
    return true
  } catch {
    return false
  }
}

/**
 * Save AG Grid table state to localStorage
 * @param tableKey - Unique identifier for the table
 * @param state - Table state object containing column, filter, and sort state
 */
export const saveTableState = (tableKey: string, state: TableStateStorage): void => {
  if (!isLocalStorageAvailable()) {
    console.warn('localStorage is not available. Table state will not be persisted.')
    return
  }

  try {
    const key = `${STORAGE_KEY_PREFIX}${tableKey}`
    localStorage.setItem(key, JSON.stringify(state))
  } catch (error) {
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      console.error('localStorage quota exceeded. Unable to save table state.')
    } else {
      console.error('Error saving table state to localStorage:', error)
    }
  }
}

/**
 * Load AG Grid table state from localStorage
 * @param tableKey - Unique identifier for the table
 * @returns Saved table state or null if not found
 */
export const loadTableState = (tableKey: string): TableStateStorage | null => {
  if (!isLocalStorageAvailable()) {
    console.warn('localStorage is not available. Unable to load table state.')
    return null
  }

  try {
    const key = `${STORAGE_KEY_PREFIX}${tableKey}`
    const savedState = localStorage.getItem(key)
    return savedState ? JSON.parse(savedState) : null
  } catch (error) {
    console.error('Error loading table state from localStorage:', error)
    return null
  }
}

/**
 * Clear table state from localStorage
 * @param tableKey - Unique identifier for the table
 */
export const clearTableState = (tableKey: string): void => {
  if (!isLocalStorageAvailable()) {
    console.warn('localStorage is not available. Unable to clear table state.')
    return
  }

  try {
    const key = `${STORAGE_KEY_PREFIX}${tableKey}`
    localStorage.removeItem(key)
  } catch (error) {
    console.error('Error clearing table state from localStorage:', error)
  }
}
